// Food + recipes surface. Calls GET /api/food (Vertex Gemini-backed)
// and renders interactions, recipe ideas, and a list of universally
// safe foods. The empty state nudges the user to add medicines first.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ComplianceNotice } from '../components/ComplianceNotice';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { ApiError } from '../components/senior/ApiError';
import { EmptyIllustration } from '../components/senior/EmptyIllustration';
import { SkeletonAnswerBlock } from '../components/senior/Skeleton';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { AppPage } from '../layouts/AppPage';

interface FoodInteraction {
  food: string;
  severity: 'AVOID' | 'LIMIT' | 'NOTE';
  reason: string;
}

interface FoodRecipe {
  name: string;
  body: string;
}

interface FoodResponse {
  medicationCount: number;
  interactions: FoodInteraction[];
  recipes: FoodRecipe[];
  safeFoods: string[];
}

const SEVERITY_TONE: Record<FoodInteraction['severity'], { label: string; class: string }> = {
  AVOID: { label: 'Avoid', class: 'food-pill--avoid' },
  LIMIT: { label: 'Limit', class: 'food-pill--limit' },
  NOTE: { label: 'Note', class: 'food-pill--note' },
};

export function Food(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const [data, setData] = useState<FoodResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/api/food/');
        const json = (await res.json()) as { ok: boolean; data?: FoodResponse };
        if (cancelled) return;
        if (json.ok && json.data) setData(json.data);
        else setError('Could not load food guidance.');
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  return (
    <AppPage title="Food and recipes" back={{ to: '/', label: 'Home' }}>
      <ComplianceNotice
        variant="clinical"
        detail="Food + drug interactions vary by person. Confirm specifics with your pharmacist, especially on warfarin, MAOIs, or grapefruit-sensitive meds."
      />
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Siggy says</p>
          <p className="food-intro__text">
            Some medicines work better, or are safer, with certain foods. Here is what fits with
            your list, plus dinner ideas that work tonight.
          </p>
        </div>
      </section>

      {loading ? <SkeletonAnswerBlock /> : null}

      {error ? (
        <ApiError
          title="Could not load food guidance"
          error={error}
          onRetry={() => window.location.reload()}
        />
      ) : null}

      {data && data.medicationCount === 0 ? (
        <EmptyIllustration
          kind="food"
          title="Add a medicine first"
          body="Food guidance is based on what you take. Add your medicines and Siggy will tailor the list to you."
          cta={
            <Link to="/medications/add-by-photo" className="home-hero__cta">
              Add a medicine
            </Link>
          }
        />
      ) : null}

      {data && data.medicationCount > 0 ? (
        <>
          {data.interactions.length > 0 ? (
            <section className="account-section">
              <h3 className="account-section__title">Foods to watch</h3>
              <ul className="food-list">
                {data.interactions.map((i) => (
                  <li key={`${i.food}-${i.severity}`} className="food-item">
                    <span className={`food-pill ${SEVERITY_TONE[i.severity].class}`}>
                      {SEVERITY_TONE[i.severity].label}
                    </span>
                    <div>
                      <p className="food-item__name">{i.food}</p>
                      <p className="food-item__reason">{i.reason}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {data.recipes.length > 0 ? (
            <section className="account-section">
              <h3 className="account-section__title">Dinner ideas tonight</h3>
              <ul className="recipe-list">
                {data.recipes.map((r) => (
                  <li key={r.name} className="recipe-card">
                    <h4 className="recipe-card__title">{r.name}</h4>
                    <p className="recipe-card__body">{r.body}</p>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {data.safeFoods.length > 0 ? (
            <section className="account-section">
              <h3 className="account-section__title">Always safe with your list</h3>
              <div className="safe-foods">
                {data.safeFoods.map((f) => (
                  <span key={f} className="safe-food-pill">
                    {f}
                  </span>
                ))}
              </div>
            </section>
          ) : null}

          {/* Diet-pattern references per Marlee UAT: "Might be good to
              also have references to recommended diets like DASH,
              Mediterranean, etc." Static links to NIH / NHLBI
              authoritative sources so we don't have to maintain the
              diet content ourselves. */}
          <section className="account-section">
            <h3 className="account-section__title">Eating patterns worth considering</h3>
            <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
              Two well-studied diets that tend to play nicely with the medicines on most senior
              lists. Talk to your doctor or a registered dietitian before making a big change.
            </p>
            <ul className="recipe-list">
              <li className="recipe-card">
                <h4 className="recipe-card__title">
                  DASH (Dietary Approaches to Stop Hypertension)
                </h4>
                <p className="recipe-card__body">
                  Lower sodium, more fruits, vegetables, and whole grains. Designed to bring blood
                  pressure down. Often recommended alongside lisinopril, losartan, amlodipine, and
                  other antihypertensives.
                </p>
                <p style={{ marginTop: 8, fontSize: 14 }}>
                  <a
                    href="https://www.nhlbi.nih.gov/education/dash-eating-plan"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-accent)', fontWeight: 600 }}
                  >
                    NHLBI DASH overview →
                  </a>
                </p>
              </li>
              <li className="recipe-card">
                <h4 className="recipe-card__title">Mediterranean</h4>
                <p className="recipe-card__body">
                  Olive oil, fish, nuts, beans, vegetables, modest poultry, very little red meat.
                  Strong evidence for heart and cognitive benefit in older adults. Watch leafy
                  greens if you take warfarin and grapefruit if you take certain statins or BP meds.
                </p>
                <p style={{ marginTop: 8, fontSize: 14 }}>
                  <a
                    href="https://www.nia.nih.gov/health/healthy-eating-nutrition-and-diet/mediterranean-diet"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-accent)', fontWeight: 600 }}
                  >
                    NIH Mediterranean diet overview →
                  </a>
                </p>
              </li>
            </ul>
          </section>
        </>
      ) : null}
    </AppPage>
  );
}
